import React, { useState, useEffect } from 'react';
import { Box, VStack, HStack, Grid, Text, Button, Heading, useToast } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { getAgenda, getBarbeiros } from '../api/mockApi';

const MotionBox = motion(Box);

const ScheduleScreen = ({ onBack }) => {
  const [schedule, setSchedule] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  // Carregar dados da agenda e barbeiros
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const formattedDate = selectedDate.toISOString().split('T')[0];
        const [agendaData, barbersData] = await Promise.all([
          getAgenda(formattedDate),
          getBarbeiros()
        ]);
        
        setSchedule(agendaData);
        setBarbers(barbersData);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        toast({
          title: 'Erro ao carregar agenda',
          description: 'Não foi possível carregar os dados da agenda.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [selectedDate, toast]);

  // Gerar datas para a semana atual
  const generateWeekDates = () => {
    const today = new Date();
    const weekDates = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      weekDates.push(date);
    }
    
    return weekDates;
  };

  const weekDates = generateWeekDates();

  // Formatar data para exibição
  const formatDate = (date) => {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'short',
      day: 'numeric',
    }).replace('.', '');
  };

  // Verificar se uma data é a selecionada
  const isDateSelected = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  // Gerar horários para exibição
  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 9;
    const endHour = 19;
    
    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push(`${hour}:00`);
      if (hour < endHour) {
        slots.push(`${hour}:30`);
      }
    }
    
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Verificar se um horário está ocupado
  const isTimeSlotOccupied = (time, barber) => {
    return schedule.some(
      item => item.time === time && item.barber === barber.name
    );
  };

  // Obter detalhes de um agendamento
  const getAppointmentDetails = (time, barber) => {
    return schedule.find(
      item => item.time === time && item.barber === barber.name
    );
  };

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      height="100vh"
      width="100%"
      bg="black"
      overflowY="auto"
      padding={6}
    >
      <VStack spacing={6} align="stretch">
        <HStack justifyContent="space-between" alignItems="center">
          <Heading size="lg">Agenda</Heading>
          <Button variant="outline" onClick={onBack}>
            Voltar
          </Button>
        </HStack>

        {/* Seletor de Datas */}
        <Box>
          <Text mb={2} fontWeight="medium">Selecione uma data:</Text>
          <HStack spacing={2} overflowX="auto" pb={2}>
            {weekDates.map((date, index) => (
              <Button
                key={index}
                onClick={() => setSelectedDate(date)}
                variant={isDateSelected(date) ? "solid" : "outline"}
                bg={isDateSelected(date) ? "yellow.400" : "transparent"}
                color={isDateSelected(date) ? "black" : "white"}
                borderColor={isDateSelected(date) ? "yellow.400" : "whiteAlpha.300"}
                minWidth="100px"
                height="60px"
              >
                <VStack spacing={0}>
                  <Text>{formatDate(date).split(' ')[0]}</Text>
                  <Text fontSize="lg" fontWeight="bold">{date.getDate()}</Text>
                </VStack>
              </Button>
            ))}
          </HStack>
        </Box>

        {/* Tabela de Horários */}
        <Box
          borderWidth="1px"
          borderColor="whiteAlpha.300"
          borderRadius="md"
          overflow="hidden"
        >
          <Grid templateColumns={`100px repeat(${barbers.length}, 1fr)`}>
            {/* Cabeçalho com nomes dos barbeiros */}
            <Box
              bg="whiteAlpha.200"
              p={3}
              borderBottomWidth="1px"
              borderColor="whiteAlpha.300"
            >
              <Text fontWeight="bold">Horário</Text>
            </Box>
            
            {barbers.map((barber) => (
              <Box
                key={barber.id}
                bg="whiteAlpha.200"
                p={3}
                borderBottomWidth="1px"
                borderLeftWidth="1px"
                borderColor="whiteAlpha.300"
              >
                <Text fontWeight="bold">{barber.name}</Text>
              </Box>
            ))}

            {/* Linhas de horários */}
            {timeSlots.map((time, timeIndex) => (
              <React.Fragment key={time}>
                <Box
                  p={3}
                  borderBottomWidth={timeIndex < timeSlots.length - 1 ? "1px" : "0"}
                  borderColor="whiteAlpha.300"
                >
                  <Text>{time}</Text>
                </Box>
                
                {barbers.map((barber) => {
                  const isOccupied = isTimeSlotOccupied(time, barber);
                  const appointment = isOccupied ? getAppointmentDetails(time, barber) : null;
                  
                  return (
                    <Box
                      key={`${barber.id}-${time}`}
                      p={3}
                      borderBottomWidth={timeIndex < timeSlots.length - 1 ? "1px" : "0"}
                      borderLeftWidth="1px"
                      borderColor="whiteAlpha.300"
                      bg={isOccupied ? "whiteAlpha.200" : "transparent"}
                    >
                      {isOccupied ? (
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium" isTruncated maxWidth="100%">
                            {appointment.clientName}
                          </Text>
                          <Text fontSize="sm" color="whiteAlpha.700">
                            {appointment.service}
                          </Text>
                        </VStack>
                      ) : (
                        <Text color="whiteAlpha.500">Disponível</Text>
                      )}
                    </Box>
                  );
                })}
              </React.Fragment>
            ))}
          </Grid>
        </Box>
      </VStack>
    </MotionBox>
  );
};

export default ScheduleScreen;
